import { ParsedUrlQuery } from 'querystring';

import { resolveHref } from 'next/dist/client/resolve-href';
import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useCallback } from 'react';

import { useAppContext } from '../../components/providers/app-context';

// Mapeamento de rotas do sistema de ficheiros
export const originalRoutes = {
  contacts: '/contacts',
  'legal-mentions': '/legal-mentions',
  'terms-and-conditions': '/terms-and-conditions',
  'about-us': '/about-us',
  nps: '/nps',
  'unsubscribe-emails': '/unsubscribe-emails/[token]',
  categories: '/auto-parts/categories',
  'categories-with-vehicle': '/auto-parts/[category]/categories',
  'sub-categories': '/auto-parts/[category]',
  'sub-categories-with-vehicle': '/auto-parts/[category]/category/[slug]',
  brands: '/auto-parts/brands',
  'brands-with-part': '/auto-parts/[category]/[subCategory]/brands',
  models: '/auto-parts/models/[brand]',
  'models-with-part': '/auto-parts/[category]/[subCategory]/models/[brand]',
  versions: '/auto-parts/versions/[model]',
  'versions-with-part': '/auto-parts/[category]/[subCategory]/versions/[model]',
  vehicle: '/auto-parts/vehicle/[vehicle]',
  index: '/',
  faqs: '/faqs',
  faqs_group: '/faqs/[group]',
  login: '/login',
  signup: '/signup',
  'password-reset': '/password-reset',
  'password-reset-confirm': '/password-reset-confirm/[token]',
  products: '/auto-parts/[category]/[subCategory]/[vehicle]',
  search: '/auto-parts/search',
  product: '/auto-parts/[category]/[subCategory]',
  'create-part-alert': '/create-part-alert',
  'private-area': '/private-area',
  'private-area-purchases': '/private-area',
  'private-area-purchases-order': '/private-area',
  'private-area-purchases-return': '/private-area',
  'private-area-purchases-cancel': '/private-area',
  'private-area-claims': '/private-area',
  'private-area-claims-detail': '/private-area',
  'private-area-my-cars': '/private-area',
  'private-area-part-alerts': '/private-area',
  'private-area-part-alerts-create': '/private-area',
  'private-area-account-details': '/private-area',
  'private-area-shipping-address': '/private-area',
  'private-area-billing-address': '/private-area',
  'private-area-reset-password': '/private-area',
  'private-area-advanced-search': '/private-area',
  'private-area-current-wallet': '/private-area',
  'private-area-wallet': '/private-area',
  'private-area-client-purchases': '/private-area',
  'private-area-client-claims': '/private-area',
  'private-area-purchase-wallet': '/private-area',
  'private-area-purchase-wallet-order': '/private-area',
  'private-area-purchase-wallet-return': '/private-area',
  'private-area-client-purchases-order': '/private-area',
  'private-area-conexions': '/private-area',
  checkout: '/checkout',
  'checkout-login': '/checkout',
  'checkout-shipping': '/checkout',
  'checkout-payment': '/checkout',
  'checkout-thanks': '/checkout',
  'checkout-payment-processing': '/checkout',
  pro: '/pro',
  'eco-repair-score': '/eco-repair-score',
  'advanced-search': '/pro/v2',
  'advanced-search-products-search': '/pro/v2/search',
  'advanced-search-product': '/pro/v2/[prodCategory]/[prodSlug]',
  'service-box': '/sb',
  'service-box-search': '/sb/search',
  'service-box-product': '/sb/[prodCategory]/[prodSlug]',
  'service-box-login': '/service-box/lg',
  "service-box-error": "/sb/error"
};

const useLinkProps = (): ((
  id: keyof typeof originalRoutes,
  query?: ParsedUrlQuery | { [key: string]: any },
  hash?: string
) => { href: LinkProps['href']; as: LinkProps['as'] }) => {
  const { t } = useTranslation();
  const {
    features: { isBrandEnabled },
    brand,
  } = useAppContext();
  const router = useRouter();

  return useCallback(
    (
      id: keyof typeof originalRoutes,
      query?: { [key: string]: string },
      hash?: string
    ) => {
      let currentId = id;
      let currentQuery: { [key: string]: string } & { brand?: string } = {
        ...query,
      };

      if (isBrandEnabled) {
        switch (id) {
          case 'brands':
            currentId = 'models';
            currentQuery.brand = brand;
            break;
          case 'brands-with-part':
            currentId = 'models-with-part';
            currentQuery.brand = brand;
            break;
        }
      }

      const asQuery = { ...currentQuery };
      let manualAppend = '';
      Object.keys(asQuery).forEach(key => {
        if (Array.isArray(asQuery[key])) {
          manualAppend += (manualAppend || Object.keys(asQuery).length > 1 ? '&' : '?') + asQuery[key].map(v => `${key}=${v}`).join('&');
          delete asQuery[key];
        }
      });

      let asPath = resolveHref(
        router,
        {
          pathname: t(`routes:${currentId}`),
          query: asQuery,
          hash,
        },
        true
      )[1] as string;

      if (manualAppend) {
        asPath += asPath.includes('?') ? (manualAppend.startsWith('?') ? manualAppend.replace('?', '&') : `&${manualAppend}`) : (manualAppend.startsWith('?') ? manualAppend : `?${manualAppend}`);
      }

      return {
        href: {
          pathname: originalRoutes[currentId],
          query: currentQuery,
          hash,
        },
        as: asPath,
      };
    },
    [isBrandEnabled, router, t, brand]
  );
};

export const useResolveLink = () => {
  const router = useRouter();
  const { t } = useTranslation();

  return useCallback(
    (id: keyof typeof originalRoutes, query = '') => {
      return resolveHref(
        router,
        {
          pathname: t(`routes:${id}`),
          query: query,
        },
        true
      )[1];
    },
    [router, t]
  );
};

export default useLinkProps;
